import { Directive, OnDestroy, OnInit } from '@angular/core';
import { BehaviorSubject, combineLatest, Observable, Subscription } from 'rxjs';
import { FormGroup, UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { distinctUntilChanged, map, startWith, switchMap, take } from 'rxjs/operators';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import { keyBy } from 'lodash';

import { VariationValue } from '@harnessio/ff-javascript-client-sdk/dist/types';
import {
  DsButtonVariants,
  DsModalService,
  DsModalVariant,
  SortDir,
  TableCellData,
  TableDropdownItem,
  TableRow,
  TableSortEvent,
} from '@levelaccess/design-system';
import {
  IDclConfig,
  ITableColumn,
  ITableConfig,
  ITableEmptyState,
  ITableRow,
  SortEvent,
} from '../../../components/table/ngb-table/utilities/ngb-table.interface';
import { IPaginationParams } from '../../../interfaces/pagination.interface';
import { ITenant, ITenantsResponse, ITenantWithPackageData } from '../../../../../shared/interfaces/tenant.interface';
import { $sortingOrder } from '../../../../../shared/constants/sort';
import { $paginationParams } from '../../../constants/pagination-params';
import { DEFAULT_ENTRY_LIMITS } from '../../../constants/form.constants';
import { AclSecurityAdapter } from '../../../services/acl.service';
import { FunctionalArea, SecureAction } from '../../../../../shared/constants/security';
import { AppConfigService } from '../../../services/app-config.service';
import { TranslateService } from '../../../translate/translate.service';
import { TenantService } from '../../../services/tenant.service';
import { ErrorHandlerService } from '../../../services/error-handler.service';
import { UserAclService } from '../../../services/user-acl.service';
import { NotificationService } from '../../../services/notification.service';
import { NotificationPosition } from '../../../models/notification.model';
import { LoadErrorHandler } from '../../../services/load-error-handler';
import { PaginationService } from '../../../services/pagination.service';
import { RoutingService } from '../../../services/routing.service';
import { AngularUtility } from '../../../utility/angular.utility';
import { $tenant, $tenants, $tenantMetadata, TenantStatus } from '../../../../../shared/constants/tenant';
import { NgbTableUtilities } from '../../../components/table/ngb-table/utilities/ngb-table.utilities';
import { IDropdownItem } from '../../../components/table/ngb-table/cells/dropdown-cell/dropdown-cell.component';
import { SharedCommonUtility } from '../../../../../shared/utils/common.utility';
import { Api } from '../../../../../shared/constants/api';
import { ILinkCellConfig } from '../../../components/table/ngb-table/cells/link-cell/link-cell.component';
import { ITextCellConfig } from '../../../components/table/ngb-table/cells/text-cell/text-cell.component';
import { $academy } from '../../../../../shared/constants/academy';
import { IPackageAddonName, IPackageInstanceIdName } from '../../../../../shared/interfaces/packaging.interface';
import { PackageService } from '../../../services/package.service';
import { $package, $packageAddon, $packageInstance } from '../../../../../shared/constants/packaging';
import { FeatureFlagService } from '../../../services/feature-flag/feature-flag.service';
import { FeatureFlagCollection } from '../../../../../shared/interfaces/feature-flag.interface';
import { UserService } from '../../../services/user.service';
import { PromptConfirmationModalComponent } from '../../../components/modals/prompt-confirmation-modal/prompt-confirmation-modal.component';
import { PromptConfirmationModalOptions } from '../../../interfaces/modal-dialog.interface';
import { ORGANIZATIONS_ADDONS_SEPARATOR, OrganizationsTableColumn } from './organizations-table/organizations-table.constants';

export enum FormField {
  SEARCH = 'search',
}

export interface IOrganizationsPaginationParams extends IPaginationParams {
  [FormField.SEARCH]: string;
}

@Directive()
export abstract class AbstractOrganizationsComponent implements OnInit, OnDestroy {
  public static readonly PAGINATION_PAGE_SIZE: number = 10;
  public readonly pageSizeOptions: number[] = [...DEFAULT_ENTRY_LIMITS];
  public readonly FormField: typeof FormField = FormField;

  protected readonly sortableColumns: string[] = [OrganizationsTableColumn.NAME];
  protected readonly formFields: string[] = [FormField.SEARCH];
  protected readonly defaultPaginationParams: IOrganizationsPaginationParams = {
    [$paginationParams.pageNumber]: 1,
    [$paginationParams.pageSize]: undefined,
    [$paginationParams.sortBy]: undefined,
    [$paginationParams.direction]: undefined,
    [FormField.SEARCH]: undefined,
  };

  protected subscription: Subscription = new Subscription();
  protected reloadTenants$: BehaviorSubject<void> = new BehaviorSubject(null);
  protected paginationParams$: BehaviorSubject<IOrganizationsPaginationParams> = new BehaviorSubject(
    this.defaultPaginationParams,
  );

  public form: UntypedFormGroup;
  public tableConfig: ITableConfig;
  public tenantsData$: Observable<ITenantsResponse>;
  public totalRows$: Observable<number>;
  public tableRows$: Observable<ITableRow[]>;
  public dsTableRows$: Observable<TableRow[]>;
  public useDsTable$: Observable<boolean>;
  public canCreateTenant: boolean = false;
  public canUpdateTenant: boolean = false;
  public canDeleteTenant: boolean = false;
  public tenantBaseUrl: string = '';
  public pageTitle: string = '';
  public pageNumber: number;
  public pageSize: number = AbstractOrganizationsComponent.PAGINATION_PAGE_SIZE;
  public dsSortColumnKey: string | null = null;
  public dsSortDirection: SortDir = null;
  public sortingOrder: Record<string, $sortingOrder>;

  public canReadWorkspace: boolean = false;
  public canReadDigitalAssets: boolean = false;

  private isSelfManagementEnabled$: Observable<boolean>;
  private tenantDecommissionFF$: Observable<boolean>;

  abstract getTenants$(params: IOrganizationsPaginationParams): Observable<ITenantsResponse>;
  abstract getTableEmptyState(): ITableEmptyState;

  protected constructor(
    protected appConfigService: AppConfigService,
    protected translateService: TranslateService,
    protected tenantService: TenantService,
    protected formBuilder: UntypedFormBuilder,
    protected router: Router,
    protected activatedRoute: ActivatedRoute,
    protected errorHandlerService: ErrorHandlerService,
    protected userAclService: UserAclService,
    protected notificationService: NotificationService,
    protected loadErrorHandler: LoadErrorHandler,
    protected paginationService: PaginationService,
    protected routingService: RoutingService,
    protected packageService: PackageService,
    protected featureFlagService: FeatureFlagService,
    protected userService: UserService,
    protected dsModalService: DsModalService,
  ) {
    this.pageTitle = this.routingService.getPageTitle();
    this.form = this.buildForm();
    this.tenantBaseUrl = this.appConfigService.getTenantBaseUrl();

    this.subscribeToPermission(FunctionalArea.tenants, SecureAction.create, 'canCreateTenant', true);
    this.subscribeToPermission(FunctionalArea.tenants, SecureAction.update, 'canUpdateTenant', true);
    this.subscribeToPermission(FunctionalArea.tenants, SecureAction.delete, 'canDeleteTenant', true);
    this.subscribeToPermission(FunctionalArea.digital_properties, SecureAction.read, 'canReadDigitalAssets', false);
    this.subscribeToPermission(FunctionalArea.workspaces, SecureAction.read, 'canReadWorkspace', false, () =>
      this.reloadTenants$.next(),
    );
    this.isSelfManagementEnabled$ = this.featureFlagService
      .variation$(FeatureFlagCollection.selfManageOrgHierarchy, false)
      .pipe(map((value: VariationValue) => value === true));
    this.tenantDecommissionFF$ = this.featureFlagService
      .variation$(FeatureFlagCollection.tenantDecommission, false)
      .pipe(map((value: VariationValue) => value === true));
  }

  private buildForm(): FormGroup {
    return this.formBuilder.group({
      [FormField.SEARCH]: this.formBuilder.control(''),
    });
  }

  private subscribeToPermission(
    functionalArea: FunctionalArea,
    action: SecureAction,
    targetProperty: keyof this,
    useEntityLevel: boolean = false,
    postSubscribe?: () => void,
  ): void {
    this.subscription.add(
      this.userAclService
        .createCheckAccessForCurrentUser()
        .pipe(
          map((adapter: AclSecurityAdapter): boolean => {
            let permissionAdapter = adapter;
            if (useEntityLevel) {
              permissionAdapter = adapter.useEntityLevel();
            }
            return permissionAdapter.useFunctionalActions({ [functionalArea]: [action] }).check();
          }),
        )
        .subscribe((hasPermission: boolean) => {
          (this[targetProperty] as any) = hasPermission;
          postSubscribe?.();
        }),
    );
  }

  public ngOnInit(): void {
    this.tenantsData$ = this.getTenantData$();
    this.totalRows$ = this.getTotalRows$();
    this.tableRows$ = this.getTableRows$();
    this.dsTableRows$ = this.getDsTableRows$();
    this.useDsTable$ = this.featureFlagService
      .variation$(FeatureFlagCollection.dsTable, false)
      .pipe(
        map(Boolean),
        startWith(Boolean(this.featureFlagService.variation(FeatureFlagCollection.dsTable, false))),
        distinctUntilChanged(),
      );

    this.subscribeToPaginationParams();
    this.subscribeToFormValueChanges();

    this.updatePaginationParamsFromQueryParams();
  }

  private getTenantData$(): Observable<ITenantsResponse> {
    return combineLatest([this.paginationParams$, this.reloadTenants$]).pipe(
      switchMap(([params, _]: [IOrganizationsPaginationParams, void]) => this.getTenants$(params)),
      AngularUtility.shareRef<ITenantsResponse>(),
    );
  }

  private getTotalRows$(): Observable<number> {
    return this.tenantsData$.pipe(map((data: ITenantsResponse): number => data[$tenants._total]));
  }

  private combineTenantPackageName(tenantsData: ITenantsResponse): ITenantWithPackageData[] {
    const packagesByTenantId: Record<string, IPackageInstanceIdName> = keyBy(
      tenantsData[$tenants.packages],
      $packageInstance.tenantId,
    );

    const addPackageToTenant = (tenant: ITenant): ITenantWithPackageData => ({
      ...tenant,
      [$tenantMetadata.packageData]: packagesByTenantId[tenant[$tenant._id]],
    });

    return tenantsData[$tenants.tenants].map(addPackageToTenant);
  }

  private getTableRows$(): Observable<ITableRow[]> {
    return this.mapTenants$(
      (tenant: ITenant, isSelfManagementEnabled: boolean, isTenantDecommissionEnabled: boolean): ITableRow =>
        this.buildTableRow(tenant, isSelfManagementEnabled, isTenantDecommissionEnabled),
    );
  }

  private getDsTableRows$(): Observable<TableRow[]> {
    return this.mapTenants$((tenant: ITenant, isSelfManagementEnabled: boolean, isTenantDecommissionEnabled: boolean): TableRow =>
      this.buildDsTableRow(tenant, isSelfManagementEnabled, isTenantDecommissionEnabled),
    );
  }

  private mapTenants$<T>(
    buildRow: (tenant: ITenant, isSelfManagementEnabled: boolean, isTenantDecommissionEnabled: boolean) => T,
  ): Observable<T[]> {
    return combineLatest([this.tenantsData$, this.isSelfManagementEnabled$, this.tenantDecommissionFF$]).pipe(
      map(([tenantsData, isSelfManagementEnabled, isTenantDecommissionEnabled]: [ITenantsResponse, boolean, boolean]): T[] =>
        this.combineTenantPackageName(tenantsData).map((tenant: ITenant): T =>
          buildRow(tenant, isSelfManagementEnabled, isTenantDecommissionEnabled),
        ),
      ),
    );
  }

  private buildDsTableRow(tenant: ITenant, isSelfManagementEnabled: boolean, isTenantDecommissionEnabled: boolean): TableRow {
    const tenantName: string = tenant[$tenant.name];
    const isSuspended: boolean = tenant[$tenant.status] === TenantStatus.suspended;
    const dropdownItems: TableDropdownItem[] = this.buildDropdownItems(
      tenant,
      isSelfManagementEnabled,
      isTenantDecommissionEnabled,
    ).map((item: IDropdownItem): TableDropdownItem => ({
      label: item.label,
      onSelect: (): void => item.clickListener(),
    }));

    const cells: Record<string, TableCellData> = {
      [OrganizationsTableColumn.NAME]: isSuspended
        ? { textLink: { text: tenantName } }
        : {
            textLink: { text: tenantName, url: this.buildTenantUrl(tenant) },
            attributes: { target: '_blank' },
          },
      [OrganizationsTableColumn.PACKAGE]: { value: this.getPackageColumnValue(tenant) },
      [OrganizationsTableColumn.ADDONS]: {
        value: this.getPackageAddonNames(tenant).join(ORGANIZATIONS_ADDONS_SEPARATOR),
      },
      [OrganizationsTableColumn.SSO_CONFIG]: this.getSsoDsCell(tenant),
      [OrganizationsTableColumn.ACADEMY_CONFIG]: this.getAcademyDsCell(tenant),
      [OrganizationsTableColumn.ACTIONS]: {
        dropdown: {
          toggle: { ariaLabel: this.translateService.instant('chat_actions_for', tenantName) },
          items: dropdownItems,
        },
      },
    };

    return {
      key: tenant[$tenant._id],
      data: Object.fromEntries(
        Object.entries(cells).map(([columnKey, cell]: [string, TableCellData]): [string, TableCellData] => [
          columnKey,
          { ...cell, styles: { ...cell.styles, verticalAlign: 'top' } },
        ]),
      ),
    };
  }

  private getSsoDsCell(tenant: ITenant): TableCellData {
    if (tenant[$tenant.status] === TenantStatus.suspended) {
      return { value: this.translateService.instant('tenant_suspended') };
    }

    if (SharedCommonUtility.notNullish(tenant[$tenant.ssoConfig])) {
      return { value: this.translateService.instant('configured'), attributes: { configured: true } };
    }

    if (this.canCreateTenant) {
      return this.buildSetupButtonCell(tenant, 'set_up_sso', (): void => this.goToConfigureSso(tenant[$tenant._id]));
    }

    return {};
  }

  private getAcademyDsCell(tenant: ITenant): TableCellData {
    if (tenant[$tenant.status] === TenantStatus.suspended) {
      return { value: this.translateService.instant('tenant_suspended') };
    }

    if (SharedCommonUtility.isNullish(tenant[$tenant.academyConfig]?.[$academy.externalPackageSignupField])) {
      return this.buildSetupButtonCell(tenant, 'set_up_academy', (): void => this.setAcademy(tenant));
    }

    return { value: this.translateService.instant('configured'), attributes: { configured: true } };
  }

  private buildSetupButtonCell(tenant: ITenant, labelKey: string, onClick: () => void): TableCellData {
    const label: string = this.translateService.instant(labelKey);

    return {
      button: {
        label,
        ariaLabel: `${label}: ${tenant[$tenant.name]}`,
        variant: DsButtonVariants.tertiary,
        onClick,
      },
    };
  }

  private buildTableRow(tenant: ITenant, isSelfManagementEnabled: boolean, isTenantDecommissionEnabled: boolean): ITableRow {
    const isSuspended: boolean = tenant[$tenant.status] === TenantStatus.suspended;

    return {
      originalData: tenant,
      data: {
        [OrganizationsTableColumn.NAME]: isSuspended
          ? NgbTableUtilities.textCell({ text: tenant[$tenant.name] })
          : NgbTableUtilities.externalLinkCell({
              text: tenant[$tenant.name],
              showExternalLinkIcon: true,
              attributes: {
                href: this.buildTenantUrl(tenant),
              },
            }),
        [OrganizationsTableColumn.PACKAGE]: NgbTableUtilities.textCell({ text: this.getPackageColumnValue(tenant) }),
        [OrganizationsTableColumn.ADDONS]: NgbTableUtilities.htmlCell({
          text: this.getPackageAddonNames(tenant).join('<br />'),
        }),
        [OrganizationsTableColumn.SSO_CONFIG]: this.getSsoConfigCell(tenant),
        [OrganizationsTableColumn.ACADEMY_CONFIG]: this.getAcademyConfigCell(tenant),
        actions: NgbTableUtilities.dropdownCell({
          ellipsisIcon: true,
          classes: ['btn-link', 'px-0'],
          dropdownItems: this.buildDropdownItems(tenant, isSelfManagementEnabled, isTenantDecommissionEnabled),
        }),
      },
    };
  }

  private shouldShowManagementDetailsButton(isSelfManagementEnabled: boolean): boolean {
    if (!this.canReadWorkspace) {
      return false;
    }

    if (isSelfManagementEnabled) {
      return this.canReadDigitalAssets;
    }

    return true;
  }

  private buildDropdownItems(
    tenant: ITenant,
    isSelfManagementEnabled: boolean,
    isTenantDecommissionEnabled: boolean,
  ): IDropdownItem[] {
    const dropdownItems: IDropdownItem[] = [];
    const isSuspended: boolean = tenant[$tenant.status] === TenantStatus.suspended;

    if (this.shouldShowManagementDetailsButton(isSelfManagementEnabled)) {
      dropdownItems.push({
        label: this.translateService.instant('management_details'),
        clickListener: (): void => this.viewTenant(tenant),
      });
    }

    if (this.canUpdateTenant) {
      dropdownItems.push({
        label: this.translateService.instant(`action_edit`),
        clickListener: (): void => this.editTenant(tenant),
      });
    }

    if (this.canUpdateTenant && SharedCommonUtility.notNullish(tenant[$tenant.ssoConfig])) {
      dropdownItems.push({
        label: this.translateService.instant(`sso_configuration`),
        clickListener: (): void => this.goToConfigureSso(tenant[$tenant._id]),
      });
    }

    if (this.canUpdateTenant) {
      if (isSuspended) {
        dropdownItems.push({
          label: this.translateService.instant('resume_tenant'),
          clickListener: (): void => this.confirmAndResumeTenant(tenant),
        });
      } else if (tenant[$tenant.status] === TenantStatus.active) {
        dropdownItems.push({
          label: this.translateService.instant('suspend_tenant'),
          clickListener: (): void => this.confirmAndSuspendTenant(tenant),
        });
      }
    }

    if (this.canDeleteTenant && isTenantDecommissionEnabled) {
      dropdownItems.push({
        label: this.translateService.instant('decommission_tenant'),
        clickListener: (): void => this.confirmAndDecommissionTenant(tenant),
      });
    }

    return dropdownItems;
  }

  private viewTenant(tenant: ITenant): void {
    this.router
      .navigate([tenant[$tenant._id]], { relativeTo: this.activatedRoute })
      .catch(this.errorHandlerService.handleRoutingError.bind(this.errorHandlerService));
  }

  private editTenant(tenant: ITenant): void {
    this.router
      .navigate([tenant[$tenant._id], Api.edit], { relativeTo: this.activatedRoute })
      .catch(this.errorHandlerService.handleRoutingError.bind(this.errorHandlerService));
  }

  private goToConfigureSso(tenantId: string): void {
    this.router
      .navigate([tenantId, Api.ssoConfig], { relativeTo: this.activatedRoute })
      .catch(this.errorHandlerService.handleRoutingError.bind(this.errorHandlerService));
  }

  private confirmAndDecommissionTenant(tenant: ITenant): void {
    this.confirmAndRunTenantAction(tenant, 'decommission', () => this.tenantService.decommissionTenant(tenant[$tenant._id]));
  }

  private confirmAndSuspendTenant(tenant: ITenant): void {
    this.confirmAndRunTenantAction(tenant, 'suspend', () => this.tenantService.suspendTenant(tenant[$tenant._id]));
  }

  private confirmAndResumeTenant(tenant: ITenant): void {
    this.confirmAndRunTenantAction(tenant, 'resume', () => this.tenantService.resumeTenant(tenant[$tenant._id]));
  }

  private confirmAndRunTenantAction(
    tenant: ITenant,
    action: 'decommission' | 'suspend' | 'resume',
    serviceCall: () => Observable<ITenant | void>,
  ): void {
    const modalRef = this.dsModalService.open(PromptConfirmationModalComponent, DsModalVariant.center);
    const modal: PromptConfirmationModalComponent = modalRef.componentInstance as PromptConfirmationModalComponent;
    const tenantName: string = tenant[$tenant.name];
    const modalOptions: PromptConfirmationModalOptions = {
      headerKey: `${action}_tenant_modal_title`,
      descriptionKey: `${action}_tenant${action === 'decommission' ? '_prompt' : '_modal'}_description`,
      descriptionParams: [tenantName],
      listHeaderKey: null,
      listItems: [],
      promptConfirmKey: `${action}_tenant_type_to_confirm`,
      promptConfirmParams: [tenantName],
      promptInputKey: `${action}_tenant_prompt_input`,
      promptInputParams: [tenantName],
      confirmButtonKey: `${action}_tenant_confirm`,
    };
    modal.init(modalOptions);

    this.subscription.add(
      modal.confirm.pipe(take(1)).subscribe((): void => {
        this.subscription.add(
          serviceCall().subscribe({
            next: (): void => {
              this.notificationService.success(
                this.translateService.instant(`${action}_tenant_success`),
                NotificationPosition.Toast,
              );
              this.reloadTenants$.next();
            },
            error: (error: HttpErrorResponse): void => {
              this.loadErrorHandler.handleError(`${action}_tenant_error`, error, this.constructor.name);
            },
          }),
        );
      }),
    );
  }

  private getSsoConfigCell(tenant: ITenant): IDclConfig<ILinkCellConfig | ITextCellConfig> {
    const isSuspended: boolean = tenant[$tenant.status] === TenantStatus.suspended;

    if (isSuspended) {
      return NgbTableUtilities.textCell({
        text: this.translateService.instant('tenant_suspended'),
      });
    }

    if (SharedCommonUtility.notNullish(tenant[$tenant.ssoConfig])) {
      return NgbTableUtilities.textCell({
        text: this.translateService.instant('configured'),
        icon: {
          iconName: 'check-outline',
          iconHeight: '1rem',
          iconWidth: '1rem',
        },
      });
    }

    if (this.canCreateTenant) {
      return NgbTableUtilities.linkCell({
        text: this.translateService.instant('set_up_sso'),
        attributes: { routerLink: `./${tenant[$tenant._id]}/${Api.ssoConfig}` },
      });
    }

    return NgbTableUtilities.emptyCell();
  }

  private getAcademyConfigCell(tenant: ITenant): IDclConfig<ILinkCellConfig | ITextCellConfig> {
    const needsSetup: boolean = SharedCommonUtility.isNullish(
      tenant[$tenant.academyConfig]?.[$academy.externalPackageSignupField],
    );
    const isSuspended: boolean = tenant[$tenant.status] === TenantStatus.suspended;

    if (isSuspended) {
      return NgbTableUtilities.textCell({
        text: this.translateService.instant('tenant_suspended'),
      });
    }

    if (needsSetup) {
      return NgbTableUtilities.buttonCell({
        text: this.translateService.instant('set_up_academy'),
        classes: ['btn-link', 'text-underline', 'setup_academy_btn', 'p-0'],
        styles: { fontSize: 'inherit' },
        listeners: {
          click: this.setAcademy.bind(this, tenant),
        },
      });
    }

    return NgbTableUtilities.textCell({
      text: this.translateService.instant('configured'),
      icon: {
        iconName: 'check-outline',
        iconHeight: '1rem',
        iconWidth: '1rem',
      },
    });
  }

  public setAcademy(tenant: ITenant): void {
    this.editTenant(tenant);
  }

  private subscribeToPaginationParams(): void {
    this.subscription.add(
      this.paginationParams$.subscribe((params: IOrganizationsPaginationParams) => {
        this.pageNumber = params[$paginationParams.pageNumber];
        this.pageSize = params[$paginationParams.pageSize] ?? AbstractOrganizationsComponent.PAGINATION_PAGE_SIZE;
        this.dsSortColumnKey = params[$paginationParams.sortBy] ?? OrganizationsTableColumn.NAME;
        this.dsSortDirection = (params[$paginationParams.direction] as SortDir) ?? $sortingOrder.asc;
        this.sortingOrder = SharedCommonUtility.notNullish(params[$paginationParams.sortBy])
          ? { [params[$paginationParams.sortBy]]: params[$paginationParams.direction] }
          : {};

        this.paginationService.updateUrlQueryParams(params);
        this.paginationService.updateFormValues(params, this.form, this.formFields);
        this.tableConfig = this.buildTableConfig();
      }),
    );
  }

  private subscribeToFormValueChanges(): void {
    this.subscription.add(
      this.paginationService.getFormValueChanges$(this.form, this.formFields).subscribe((params: Params) => {
        this.updatePaginationParams({
          ...params,
          [$paginationParams.pageNumber]: 1,
        });
      }),
    );
  }

  private updatePaginationParamsFromQueryParams(): void {
    const params: IOrganizationsPaginationParams = this.paginationService.getPaginationParamsFromQueryParams(
      this.defaultPaginationParams,
      this.sortableColumns,
      this.formFields,
    );

    this.updatePaginationParams({
      ...params,
      [$paginationParams.pageSize]: this.normalizePageSize(params[$paginationParams.pageSize]),
    });
  }

  private normalizePageSize(pageSize: number | undefined): number | undefined {
    return this.pageSizeOptions.includes(pageSize) ? pageSize : undefined;
  }

  private updatePaginationParams(update: Partial<IOrganizationsPaginationParams> = {}): void {
    this.paginationParams$.next({
      ...this.paginationParams$.value,
      ...update,
    });
  }

  public onPageNumberChanges(pageNumber: number): void {
    this.updatePaginationParams({
      [$paginationParams.pageNumber]: pageNumber,
    });
  }

  public onPageSizeChange(pageSize: number): void {
    this.updatePaginationParams({
      [$paginationParams.pageNumber]: 1,
      [$paginationParams.pageSize]: pageSize,
    });
  }

  public onDsSort(event: TableSortEvent): void {
    const hasDirection: boolean = event.sortDirection === 'asc' || event.sortDirection === 'desc';

    this.updatePaginationParams({
      [$paginationParams.pageNumber]: 1,
      [$paginationParams.sortBy]: hasDirection ? event.columnKey : undefined,
      [$paginationParams.direction]: hasDirection ? (event.sortDirection as $sortingOrder) : undefined,
    });
  }

  public onSort(event: SortEvent): void {
    this.updatePaginationParams({
      [$paginationParams.pageNumber]: 1,
      [$paginationParams.sortBy]: event.column,
      [$paginationParams.direction]: event.direction,
    });
  }

  public ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  public buildTableConfig(): ITableConfig {
    const columns: Record<string, ITableColumn> = {
      [OrganizationsTableColumn.NAME]: {
        translationKey: 'tenant_name',
        sortingEnabled: true,
        styles: {
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          width: '25%',
        },
      },
      [OrganizationsTableColumn.PACKAGE]: {
        translationKey: 'label_package',
        styles: {
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          width: '30%',
        },
      },
      [OrganizationsTableColumn.ADDONS]: {
        translationKey: 'label_addons',
        styles: {
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          width: '15%',
        },
      },
      [OrganizationsTableColumn.SSO_CONFIG]: {
        translationKey: 'sso',
        styles: {
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          width: '15%',
        },
      },
      [OrganizationsTableColumn.ACADEMY_CONFIG]: {
        translationKey: 'academy',
        styles: {
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          width: '15%',
        },
      },
      actions: {
        translationKey: 'table_column_actions',
        styles: {
          width: '15%',
        },
      },
    };

    return {
      columns,
      caption: this.translateService.instant('table_caption_tenant_list'),
      emptyState: this.getTableEmptyState(),
    };
  }

  private buildTenantUrl(tenant: ITenant): string {
    return `https://${tenant[$tenant.domain]}.${this.tenantBaseUrl}`;
  }

  private getPackageColumnValue(tenant: ITenantWithPackageData): string {
    const packageData: IPackageInstanceIdName = tenant[$tenantMetadata.packageData];
    return SharedCommonUtility.notNullish(packageData)
      ? packageData[$packageInstance.packageDetails]?.[$package.name]
      : this.translateService.instant('no_package');
  }

  private getPackageAddonNames(tenant: ITenantWithPackageData): string[] {
    const packageAddonNames: IPackageAddonName[] = tenant[$tenantMetadata.packageData]?.[$packageInstance.packageAddons];
    return SharedCommonUtility.notNullishOrEmpty(packageAddonNames)
      ? packageAddonNames.map((packageAddonName: IPackageAddonName): string => packageAddonName[$packageAddon.name])
      : [this.translateService.instant('not_available_acronym')];
  }
}
